// What clicking a search result should actually do.
//
// SearchModal used to map a result's type straight to a tab name and navigate
// there, which switched tabs but left the user to hunt for the very thing they
// had searched for. Every type below except `character` and `secret` has a
// detail modal that CampaignPage's openEntity() can open via focusEntity, so
// those resolve to an entity action instead.
//
// Pure and free of React/DOM so tests/search-navigation.test.js can exercise it
// in Vitest's `node` environment.

// Types whose tab renders a focusId-driven detail modal. Shared with
// CampaignPage's openEntity(), which is the other caller that has to turn an
// entity type into a tab — [[wiki-links]] and search must agree on this map or
// one of them silently stops resolving a type the other handles.
export const ENTITY_TYPE_TO_TAB = {
  quest: 'quests',
  location: 'locations',
  npc: 'npcs',
  item: 'items',
  session: 'sessions',
}

// Types that can only be navigated to.
const TAB_ONLY_TYPES = {
  character: 'character',
  secret: 'character', // secrets live in a My Character subtab
}

/**
 * @returns {{kind:'entity', type:string, id:number}
 *          |{kind:'tab', tab:string, subtab:string|null}
 *          |null} null when the type is unrecognised (the click is a no-op).
 */
export function resolveSearchAction(result, { canEdit } = {}) {
  if (!result) return null
  const { type, data } = result

  if (type === 'secret') {
    return { kind: 'tab', tab: 'character', subtab: canEdit ? 'admin-secrets' : 'secrets' }
  }

  if (TAB_ONLY_TYPES[type]) {
    return { kind: 'tab', tab: TAB_ONLY_TYPES[type], subtab: null }
  }

  const tab = ENTITY_TYPE_TO_TAB[type]
  if (!tab) return null

  // Without an id, openEntity would set focusEntity to an id no row can match:
  // the modal would never open and the stale focus would sit unconsumed. Land
  // on the tab instead, which at least gets the user to the right list.
  const id = data?.id
  if (id === undefined || id === null) return { kind: 'tab', tab, subtab: null }

  return { kind: 'entity', type, id }
}
